<script lang="ts">
	import PageGrid16 from '../components/pageGrid16.svelte';
	import type { SbImage, SbLink, StripeProductData } from '../types';
	import Image from '../components/image.svelte';
	import ProductPrice from '../parts/productPrice.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import Blok from '../blok.svelte';
	import { checkoutSession } from '$lib/state/checkoutSession.svelte';
	import { shoppingCart } from '$lib/state/shoppingCart.svelte';

	interface Props {
		content: ISbStoryData<{
			bg_color: string;
			bg_image: Array<ISbStoryData['content']>;
			product_image: SbImage;
			bundle_id: string;
			product: StripeProductData;
			product_name?: string;
			product_short_name?: string;
			value_props: Array<ISbStoryData['content']>;
			display_discount: boolean;
			price_line?: string;
			price_subline?: string;
			cta_title: string;
			behavior: 'containered' | 'fullbleed';
		}>['content'];
	}

	let { content }: Props = $props();

	let productName = $derived(
		content.product_name && content.product_name.length > 0
			? content.product_name
			: typeof content.product.data.price.product !== 'string' &&
				  !content.product.data.price.product.deleted
				? content.product.data.price.product.name
				: 'Mill Food Recycler'
	);
</script>

<section
	use:editable={content}
	class={[
		`bg-${content.bg_color} relative py-16 max-lg:px-5 lg:py-32`,
		content.behavior === 'containered' && 'overflow-hidden lg:mx-5 lg:rounded-2xl'
	]}
>
	{#if content.bg_image?.length > 0}
		<div class="absolute inset-0">
			<Blok content={content.bg_image[0]} />
		</div>
	{/if}
	<PageGrid16>
		<div
			class={[
				'z-10 col-span-full rounded-xl bg-white px-5 py-8 lg:col-start-2 lg:col-end-[16] lg:px-12 lg:py-10',
				(content.bg_color || content.bg_image?.length > 0) &&
					'gradient-border lg:shadow-[0_8px_16px_0_rgba(0,0,0,0.04) shadow-[0_8px_16px_0_rgba(0,0,0,0.08)]'
			]}
		>
			<div class="flex items-center gap-x-1 max-lg:flex-col">
				<Image
					src={content.product_image.filename}
					alt={content.product_image.alt}
					title={content.product_image.title}
					class="max-h-[132px] max-lg:mb-3"
				/>
				<div class="flex-1 max-lg:text-center">
					<h1 class="font-semibold text-brown-1">
						<span
							class="text-[24px]/[108%] font-semibold tracking-[-.24px] lg:text-[48px]/[120%] lg:tracking-[-.48px]"
							class:max-lg:hidden={content.product_short_name &&
								content.product_short_name.length > 0}>{productName}</span
						>
						<span
							class="text-[36px]/[112%] tracking-[-.36px]"
							class:lg:hidden={content.product_short_name && content.product_short_name.length > 0}
							>{content.product_short_name}</span
						>
					</h1>
					{#if content.price_line && content.price_line.length > 0}
						<p
							class="text-[20px]/[140%] tracking-[-.2px] text-brown-1 lg:text-[24px]/[140%] lg:tracking-[-.24px]"
						>
							{content.price_line}
						</p>
					{:else if content.product.data.price.unit_amount}
						<ProductPrice
							class="text-[20px]/[140%] tracking-[-.2px] max-lg:justify-center lg:text-[24px]/[140%] lg:tracking-[-.24px]"
							interval={content.product.data.price.recurring?.interval}
							price={content.product.data.price.unit_amount}
							promoCode={content.display_discount ? content.product.data.promotionCode : undefined}
						/>
					{/if}
					{#if content.price_subline && content.price_subline.length > 0}
						<p class="text-[12px]/[160%] text-brown-4 lg:text-[14px]/[160%]">
							{content.price_subline}
						</p>
					{/if}
				</div>
				{#if content.cta_title}
					<Button
						class="btn-large max-lg:mb-10 max-lg:mt-5 max-lg:w-full"
						onclick={() => {
							checkoutSession.select(content.product, {
								productBundle: content.bundle_id
							});
							shoppingCart.open();
						}}>{content.cta_title}</Button
					>
				{/if}
			</div>
			<hr class="mb-7 mt-8 bg-[#E9E7E6] max-lg:hidden" />

			<div class="flex gap-x-18 gap-y-8 max-lg:flex-col">
				{#each content.value_props as valueProp}
					<Blok content={valueProp} class="flex-1" />
				{/each}
			</div>
		</div>
	</PageGrid16>
</section>

<style lang="scss">
	.gradient-border {
		border: 1px solid transparent;
		background-image:
			linear-gradient(white, white), linear-gradient(to bottom, transparent, #e9e7e6);
		background-origin: border-box;
		background-clip: padding-box, border-box;
	}
</style>
